// ── Meal-time reminders ─────────────────────────────────────────────
// At the hour a planned meal is meant to happen, ask whether it did. Answering
// "ate it" from the notification is the whole point: ticking a meal is a
// two-second job that nobody remembers to go and do, and a diary fed only by
// meals you remembered to confirm is a diary of your memory, not your eating.
//
// Everything decidable without a device lives here — which reminders exist,
// when they fire, what they say — so it can be reasoned about and tested.

import { SLOT_TIMES } from "./healthLog";
import { MEAL_SLOTS, type MealSlot, type PlanDay } from "./types";

/** How far ahead to schedule. iOS keeps 64 pending local notifications per app
 *  and the timers use their own range, so a week of four slots (28) is a
 *  comfortable fit — and a week is as far ahead as anyone plans here. */
export const HORIZON_DAYS = 7;

const SLOT_LABEL: Record<MealSlot, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snack",
};

export type MealReminder = {
  /** Stable across reschedules, so re-running the scheduler replaces a
   *  reminder rather than stacking a second copy of it. */
  id: number;
  date: string;
  slot: MealSlot;
  /** When it fires, local time. */
  at: Date;
  title: string;
  body: string;
};

/**
 * A deterministic id for a day's slot.
 *
 * Derived rather than random so scheduling is idempotent: the same meal always
 * claims the same notification id, and rescheduling overwrites it. Days since
 * the epoch keeps it inside the 32-bit range iOS requires, and the 40000 offset
 * keeps it clear of the step timers' range (which starts at 3000).
 */
export function reminderId(date: string, slot: MealSlot): number {
  const days = Math.floor(Date.parse(`${date}T00:00:00Z`) / 86_400_000);
  return 40_000 + days * 10 + MEAL_SLOTS.indexOf(slot);
}

/** The local moment a slot's reminder fires on a given day. */
export function reminderAt(date: string, slot: MealSlot): Date {
  const { hour, minute } = SLOT_TIMES[slot];
  const at = new Date(`${date}T00:00:00`);
  at.setHours(hour, minute, 0, 0);
  return at;
}

function describe(titles: string[]): string {
  if (titles.length === 1) return titles[0];
  if (titles.length === 2) return `${titles[0]} and ${titles[1]}`;
  return `${titles[0]} and ${titles.length - 1} more`;
}

/**
 * The reminders that should be pending, given the plan.
 *
 * A slot earns one when it has at least one meal that isn't already ticked as
 * eaten — there's nothing to ask about otherwise, and asking anyway is how a
 * useful notification becomes one you swipe away without reading. Times that
 * have already passed are skipped rather than fired late: a 12:30 prompt
 * arriving at 16:00 asks about lunch you either had or didn't, hours ago.
 */
export function dueReminders(
  days: PlanDay[],
  now: Date = new Date(),
  horizonDays: number = HORIZON_DAYS
): MealReminder[] {
  const limit = new Date(now.getTime() + horizonDays * 86_400_000);
  const out: MealReminder[] = [];

  for (const day of days) {
    for (const slot of MEAL_SLOTS) {
      const meals = day.meals.filter((m) => m.slot === slot);
      if (meals.length === 0) continue;
      // Already answered — every meal in the slot is ticked.
      if (meals.every((m) => m.eaten)) continue;

      const at = reminderAt(day.date, slot);
      if (at.getTime() <= now.getTime()) continue;
      if (at.getTime() > limit.getTime()) continue;

      const titles = meals.map((m) => m.title);
      out.push({
        id: reminderId(day.date, slot),
        date: day.date,
        slot,
        at,
        title: `${SLOT_LABEL[slot]} — did you eat it?`,
        body: describe(titles),
      });
    }
  }

  return out.sort((a, b) => a.at.getTime() - b.at.getTime());
}

/** Ids that were pending but shouldn't be any more — the meal was removed, or
 *  ticked, or its day is behind us. Cancelled explicitly, because a reminder
 *  for a meal you already dealt with is worse than no reminder at all. */
export function staleReminderIds(pendingIds: number[], due: MealReminder[]): number[] {
  const keep = new Set(due.map((r) => r.id));
  // Only ever touch ids from this feature's range; the step timers schedule
  // into the same notification centre and are none of our business.
  return pendingIds.filter((id) => id >= 40_000 && !keep.has(id));
}
